import {
  EmployeeDto,
  OnboardingPeriodDto,
  OnboardingPeriodWithEnrollmentStatusDto,
  OnboardingType,
  OpenEnrollmentPeriodDto,
  QualifyingLifeEventType,
} from '@zorro/clients';
import {
  VALIDATION_MESSAGES,
  calculateElectionDates,
  maximalDateISO,
} from '@zorro/common-utils';
import {
  DateUtilInstance,
  formatDateISO,
  getDateUtil,
  getToday,
  parseDate,
  parseDateISO,
} from '@zorro/shared/formatters';
import { onboardingTypeLabelConfig } from '@zorro/types';
import { QleFormFields, getQleSchemaObject } from '@zorro/web/shared-ui';
import { UseFormReturn } from 'react-hook-form';
import * as yup from 'yup';

export const ChangeElectionWindowSchemaObject = yup.object({
  electionWindow: yup
    .tuple([yup.date().required(), yup.date().required()])
    .required(VALIDATION_MESSAGES.electionWindowRequired),
  expectedStartOfCoverage: yup
    .date()
    .required(VALIDATION_MESSAGES.expectedStartOfCoverageRequired),
});

export type ChangeElectionWindowFormFields = yup.InferType<
  typeof ChangeElectionWindowSchemaObject
>;

export const FullChangeEnrollmentSchema = yup
  .object({
    planYear: yup.string().required(VALIDATION_MESSAGES.planYearRequired),
    enrollmentType: yup
      .string()
      .oneOf(Object.values(OnboardingType))
      .required(VALIDATION_MESSAGES.enrollmentTypeRequired),
    isQleEnrollment: yup.boolean().when('enrollmentType', {
      is: OnboardingType.SPECIAL,
      then: (schema) => schema.required(),
      otherwise: (schema) => schema.optional(),
    }),
    qle: yup.mixed<QleFormFields>().when('isQleEnrollment', {
      is: true,
      then: () => getQleSchemaObject(),
      otherwise: (schema) => schema.optional(),
    }) as yup.Schema<QleFormFields | undefined>,
    enrollmentTeamId: yup.string(),
    enrollmentAgentId: yup.string().optional(),
  })
  .concat(ChangeElectionWindowSchemaObject);

// This is not best practice, please avoid using this
// The change to move this into an yup.InferType is too big,
// and will impact a lot of places.
export type ChangeEnrollmentsFormFields = ChangeElectionWindowFormFields & {
  planYear: string;
  enrollmentType: OnboardingType;
  isQleEnrollment?: boolean;
  qle?: QleFormFields;
  enrollmentTeamId?: string | undefined;
  enrollmentAgentId?: string | undefined;
};

export const formFieldStyle = {
  flex: 1,
  marginBottom: '16px',
};

export const inlineGroupStyle = {
  display: 'flex',
  gap: '16px',
  marginBottom: '16px',
};

export const stackStyle = {
  gap: '8px',
  marginBottom: '16px',
};

export const addOrEditAlertBoxText = (isSingleEmployee: boolean): string => {
  return `
  Choose the dates for ${
    isSingleEmployee ? 'the employee' : 'employees'
  } to browse and elect plans.
  Coverage generally starts on the first of the month following the election.
  Ensure the period allows adequate time for processing selections.`;
};

const safeSetDay = (date: DateUtilInstance, day: number) =>
  date.date(Math.min(day, date.daysInMonth()));

const getSEPDeadline = (
  today: DateUtilInstance,
  monthlySEPDeadline: number | null,
  isMonthlySEPDeadlineEndOfMonth: boolean | null
): number => {
  const defaultSpecialUntilDate = 20; // This is by product definition but should realistically never be used.
  if (monthlySEPDeadline) {
    return monthlySEPDeadline;
  }
  if (isMonthlySEPDeadlineEndOfMonth) {
    return today.endOf('month').date();
  }
  return defaultSpecialUntilDate;
};

export const getSuggestedElectionWindow = (
  selectedOpenEnrollment: OpenEnrollmentPeriodDto,
  enrollmentType: OnboardingType,
  monthlySEPDeadline: number | null,
  isMonthlySEPDeadlineEndOfMonth: boolean | null
): [Date, Date] | undefined => {
  const today = getToday();
  const employerSpecialUntilDate = getSEPDeadline(
    today,
    monthlySEPDeadline,
    isMonthlySEPDeadlineEndOfMonth
  );
  if (enrollmentType === OnboardingType.SPECIAL) {
    const until = getDateUtil().min(
      today.date() >= employerSpecialUntilDate
        ? safeSetDay(today.add(1, 'month'), employerSpecialUntilDate)
        : safeSetDay(today, employerSpecialUntilDate),
      selectedOpenEnrollment
        ? safeSetDay(
            parseDateISO(selectedOpenEnrollment.effectiveUntil).subtract(
              1,
              'month'
            ),
            employerSpecialUntilDate
          )
        : today.add(1, 'year')
    );
    return today.isBefore(until) ? [today.toDate(), until.toDate()] : undefined;
  }
  return [
    parseDateISO(selectedOpenEnrollment.onboardingFrom).toDate(),
    parseDateISO(selectedOpenEnrollment.onboardingUntil).toDate(),
  ];
};

export const getSuggestedExpectedStartOfCoverage = (
  selectedOpenEnrollment: OpenEnrollmentPeriodDto | undefined,
  enrollmentType: OnboardingType,
  electionWindow: [Date, Date] | undefined,
  singleEmployeeDto?: EmployeeDto
): Date | undefined => {
  if (
    selectedOpenEnrollment &&
    enrollmentType === OnboardingType.SPECIAL &&
    electionWindow?.every(Boolean)
  ) {
    const nextFirstOfMonthAfterOnboardingUntil = parseDate(electionWindow[1])
      .add(1, 'month')
      .startOf('month');

    const suggestedExpectedStartOfCoverageForSpecial = parseDateISO(
      maximalDateISO(
        formatDateISO(nextFirstOfMonthAfterOnboardingUntil),
        selectedOpenEnrollment.effectiveFrom,
        singleEmployeeDto?.eligibleFrom
      )
    );
    if (
      suggestedExpectedStartOfCoverageForSpecial.isAfter(
        selectedOpenEnrollment.effectiveUntil
      ) ||
      (singleEmployeeDto?.eligibleUntil &&
        suggestedExpectedStartOfCoverageForSpecial.isAfter(
          singleEmployeeDto?.eligibleUntil
        ))
    ) {
      return undefined;
    }
    return suggestedExpectedStartOfCoverageForSpecial.toDate();
  } else if (
    selectedOpenEnrollment &&
    enrollmentType === OnboardingType.OPEN_ENROLLMENT
  ) {
    return parseDateISO(selectedOpenEnrollment.effectiveFrom).toDate();
  }
  return undefined;
};

export const getCurrentlyEffectiveYear = (
  allOpenEnrollmentPeriods: OpenEnrollmentPeriodDto[]
) => {
  const currentYear = allOpenEnrollmentPeriods.find((oep) =>
    getToday().isBetween(oep.effectiveFrom, oep.effectiveUntil)
  );
  return currentYear || null;
};

export const getLatestPlanYear = (
  allOpenEnrollmentPeriods: OpenEnrollmentPeriodDto[]
) => {
  return allOpenEnrollmentPeriods.length > 0
    ? allOpenEnrollmentPeriods.reduce((latest, current) =>
        parseDateISO(latest.effectiveFrom).isAfter(current.effectiveFrom)
          ? latest
          : current
      )
    : null;
};

export const eligibleForSpecialOnly = (
  selectedOEP?: OpenEnrollmentPeriodDto,
  singleEmployeeDto?: EmployeeDto,
  associatedOtherOnboardingPeriodsSingleEmployee?: OnboardingPeriodDto[]
) => {
  if (
    !singleEmployeeDto ||
    !selectedOEP ||
    !associatedOtherOnboardingPeriodsSingleEmployee
  ) {
    return false;
  }

  const effectiveBeforeEligibleFrom = parseDateISO(
    selectedOEP.effectiveFrom
  ).isBefore(singleEmployeeDto?.eligibleFrom);

  const hasMatchingOpenEnrollmentPeriod =
    associatedOtherOnboardingPeriodsSingleEmployee?.some(
      (period) =>
        !period.isSpecialEnrollment &&
        period.enrollmentPeriodId === selectedOEP?.id
    );

  return effectiveBeforeEligibleFrom || hasMatchingOpenEnrollmentPeriod;
};

export const getAvailableEnrollmentTypes = (
  selectedOEP?: OpenEnrollmentPeriodDto,
  singleEmployeeDto?: EmployeeDto,
  associatedOtherOnboardingPeriodsSingleEmployee?: OnboardingPeriodDto[]
) => {
  const disableOpenEnrollment = eligibleForSpecialOnly(
    selectedOEP,
    singleEmployeeDto,
    associatedOtherOnboardingPeriodsSingleEmployee
  );
  return [
    {
      value: OnboardingType.OPEN_ENROLLMENT,
      label: onboardingTypeLabelConfig[OnboardingType.OPEN_ENROLLMENT],
      disabled: disableOpenEnrollment,
    },
    {
      value: OnboardingType.SPECIAL,
      label: onboardingTypeLabelConfig[OnboardingType.SPECIAL],
    },
  ];
};

type HandleFormStateSuggestionsData = {
  planYear?: OpenEnrollmentPeriodDto;
  enrollmentType?: OnboardingType;
  isQleEnrollment?: boolean;
  qleType?: QualifyingLifeEventType;
  qleEventDate?: Date;
  electionWindow?: [Date, Date];
};

type NestedQleFields = 'qle.type' | 'qle.occurredOn' | 'qle.otherReason';
type FieldsChanged = keyof ChangeEnrollmentsFormFields | NestedQleFields;

export const handleFormStateChange = (
  monthlySEPDeadline: number | null,
  isMonthlySEPDeadlineEndOfMonth: boolean | null,
  changeEnrollmentFormState: HandleFormStateSuggestionsData,
  fieldChanged: FieldsChanged,
  setValue: UseFormReturn<ChangeEnrollmentsFormFields>['setValue'],
  singleEmployeeDto?: EmployeeDto,
  associatedOnboardingPeriodsSingleEmployee?: OnboardingPeriodWithEnrollmentStatusDto[],
  hasLockedEnrollmentFields: boolean = false
) => {
  if (!changeEnrollmentFormState.planYear) {
    return;
  }

  if (fieldChanged === 'planYear') {
    setValue('planYear', changeEnrollmentFormState.planYear.id, {
      shouldValidate: true,
    });
    if (
      changeEnrollmentFormState.planYear &&
      eligibleForSpecialOnly(
        changeEnrollmentFormState.planYear,
        singleEmployeeDto,
        associatedOnboardingPeriodsSingleEmployee
      )
    ) {
      changeEnrollmentFormState.enrollmentType = OnboardingType.SPECIAL;
    }
  }

  if (!changeEnrollmentFormState.enrollmentType) {
    return;
  }

  if (fieldChanged === 'enrollmentType') {
    setValue('enrollmentType', changeEnrollmentFormState.enrollmentType, {
      shouldValidate: true,
    });
    if (
      changeEnrollmentFormState.enrollmentType ===
      OnboardingType.OPEN_ENROLLMENT
    ) {
      setValue('isQleEnrollment', undefined, {
        shouldValidate: true,
      });
    } else {
      setValue('isQleEnrollment', false, {
        shouldValidate: true,
      });
    }
  }

  if (fieldChanged === 'isQleEnrollment') {
    setValue('isQleEnrollment', changeEnrollmentFormState.isQleEnrollment, {
      shouldValidate: true,
    });
    if (!changeEnrollmentFormState.isQleEnrollment) {
      setValue('qle', undefined, { shouldValidate: true });
      setValue('qle.evidence', undefined, { shouldValidate: true });
      changeEnrollmentFormState.qleType = undefined;
      changeEnrollmentFormState.qleEventDate = undefined;
    }
  }

  if (fieldChanged === 'qle.type' && changeEnrollmentFormState.qleType) {
    setValue('qle.type', changeEnrollmentFormState.qleType, {
      shouldValidate: true,
    });
    if (changeEnrollmentFormState.qleType !== QualifyingLifeEventType.OTHER) {
      setValue('qle.otherReason', undefined, { shouldValidate: true });
    }
  }

  if (
    fieldChanged === 'qle.occurredOn' &&
    changeEnrollmentFormState.qleEventDate
  ) {
    setValue('qle.occurredOn', changeEnrollmentFormState.qleEventDate, {
      shouldValidate: true,
    });
  }

  if (!hasLockedEnrollmentFields) {
    if (
      changeEnrollmentFormState.qleEventDate &&
      changeEnrollmentFormState.qleType
    ) {
      if (fieldChanged === 'qle.type' || fieldChanged === 'qle.occurredOn') {
        const { electionStartDate, electionEndDate, expectedStartOfCoverage } =
          calculateElectionDates(
            changeEnrollmentFormState.qleType,
            changeEnrollmentFormState.qleEventDate,
            changeEnrollmentFormState.planYear.effectiveUntil,
            singleEmployeeDto?.eligibleUntil
          );
        if (electionEndDate) {
          setValue(
            'electionWindow',
            [electionStartDate.toDate(), electionEndDate.toDate()],
            {
              shouldValidate: true,
            }
          );
        }
        if (expectedStartOfCoverage) {
          setValue(
            'expectedStartOfCoverage',
            expectedStartOfCoverage.toDate(),
            {
              shouldValidate: true,
            }
          );
        }
      }
    } else {
      const suggestedWindow = getSuggestedElectionWindow(
        changeEnrollmentFormState.planYear,
        changeEnrollmentFormState.enrollmentType,
        monthlySEPDeadline,
        isMonthlySEPDeadlineEndOfMonth
      );
      if (suggestedWindow) {
        setValue('electionWindow', suggestedWindow, {
          shouldValidate: true,
        });
        changeEnrollmentFormState.electionWindow = suggestedWindow;
      }
      const suggestedExpectedStartOfCoverage =
        getSuggestedExpectedStartOfCoverage(
          changeEnrollmentFormState.planYear,
          changeEnrollmentFormState.enrollmentType,
          changeEnrollmentFormState.electionWindow,
          singleEmployeeDto
        );
      if (suggestedExpectedStartOfCoverage) {
        setValue('expectedStartOfCoverage', suggestedExpectedStartOfCoverage, {
          shouldValidate: true,
        });
      }
    }
  }
};

export function getSharedEnrollmentType(
  allOnboardingPeriods: OnboardingPeriodDto[]
): OnboardingType | undefined {
  if (allOnboardingPeriods.length === 0) return undefined;

  const isFirstValueSpecial = allOnboardingPeriods[0].isSpecialEnrollment;
  const homogeneity = allOnboardingPeriods.every(
    (op) => op.isSpecialEnrollment === isFirstValueSpecial
  );
  return homogeneity
    ? isFirstValueSpecial
      ? OnboardingType.SPECIAL
      : OnboardingType.OPEN_ENROLLMENT
    : undefined;
}
